<template>
    <div v-if="displayBreadcrumb" class="d-flex align-center">
        <AQButton
            variant="text"
            basic
            :size="display.mdAndDown.value ? 'small' : 'default'"
            :height="display.mdAndDown.value ? 40 : 60"
            @click="router.go(-1)"
        >
            <v-icon size="large"> mdi-chevron-left </v-icon>
        </AQButton>
        <v-breadcrumbs :items="mappedItems" divider=">" size="large">
            <template v-slot:item="{ item }">
                <v-breadcrumbs-item
                    active-class="breadcrumb-active"
                    :to="item.to"
                    :disabled="item.disabled"
                    @click="handleItemClick(item)"
                >
                    <span class="text-subtitle-2 p-0">
                        {{ renderItemText(item) }}
                    </span>
                </v-breadcrumbs-item>
            </template>
        </v-breadcrumbs>
    </div>
</template>
<script setup lang="ts">
    import { computed, onMounted, watch } from 'vue';
    import { useRouter, useRoute } from 'vue-router';
    import { useI18n } from 'vue-i18n';
    import { useDisplay } from 'vuetify';
    import type { BreadcrumbItem } from '@/store/modules/breadcrumb';
    import { InternalBreadcrumbItem } from 'vuetify/lib/components/VBreadcrumbs/VBreadcrumbs';
    import { useStore } from 'vuex';
    import AQButton from '../shared/forms/inputs/AQButton.vue';

    const emit = defineEmits<{
        (e: 'send-action', action: string): void;
    }>();

    const store = useStore();
    const route = useRoute();
    const breadcrumbs = computed(() => store.getters.breadcrumbs);
    const displayBreadcrumb = computed(() => store.getters.displayBreadcrumb);
    const client = computed(() => store.getters.client);
    const site = computed(() => store.getters.site);
    const workOrder = computed(() => store.getters.workOrder);
    const woTemplate = computed(() => store.getters.woTemplate);
    watch(
        () => client.value,
        (newClient) => {
            if (newClient?.id.length && route.params.clientId) {
                buildFromRoute();
            }
        },
        { deep: true },
    );
    const router = useRouter();
    const { t } = useI18n();
    const display = useDisplay();

    const allRoutes = router.getRoutes();
    const mappedItems = computed(() => {
        if (!breadcrumbs.value?.length) return [];
        return breadcrumbs.value.map((item: BreadcrumbItem, index: number) => {
            let toRoute = item.to;
            if (!toRoute && item.name && !item.disabled && index < breadcrumbs.value.length - 1) {
                const matchedRoute = allRoutes.find((r) => r.name === item.name);
                if (matchedRoute) {
                    const requiredParams: Record<string, string> = {};
                    const paramNames =
                        matchedRoute.path.match(/:(\w+)/g)?.map((p) => p.slice(1)) || [];
                    paramNames.forEach((paramName) => {
                        if (route.params[paramName]) {
                            requiredParams[paramName] = route.params[paramName] as string;
                        }
                    });
                    toRoute = { name: item.name, params: requiredParams };
                }
            }
            return { ...item, title: item.text, to: toRoute };
        });
    });

    const emptyBreadcrumbs = () => store.dispatch('emptyBreadcrumbs');
    const pushBreadcrumb = (breadcrumb: BreadcrumbItem) => {
        if (displayBreadcrumb.value) {
            store.dispatch('pushBreadcrumb', breadcrumb);
        }
    };

    const CRM_BREADCRUMBS: Record<string, { name: string; text: string }> = {
        'crm': { name: 'crm', text: 'crm.pageName' },
        'dashboard': { name: 'crm-dashboard', text: 'crm.dashboard.navTitle' },
        'opportunities': { name: 'crm-opportunities', text: 'crm.opportunity.navTitle' },
        'all-activities': { name: 'crm-all-activities', text: 'crm.allActivities.navTitle' },
        'access': { name: 'crm-access', text: 'crm.crmAccess.navTitle' },
        'configuration': { name: 'crm-configuration', text: 'crm.configurationCrm.navTitle' },
        'advanced-config': { name: 'crm-advanced-config', text: 'crm.advancedConfig.navTitle' },
        ':id': { name: 'crm-opportunity-detail', text: 'crm.opportunityDetail.navTitle' },
    };

    const buildFromRoute = () => {
        emptyBreadcrumbs();
        const paths = route.matched[route.matched.length - 1];
        if (!paths) return;
        const currentPath = paths.path.split('/');
        currentPath.shift();
        currentPath.forEach((routeSegment) => {
            if (currentPath[0] === 'crm') {
                const crumb = CRM_BREADCRUMBS[routeSegment];
                if (crumb) {
                    pushBreadcrumb(crumb);
                }
                return;
            }
            switch (routeSegment) {
                case 'work-order-templates':
                    pushBreadcrumb({
                        name: 'work-order-templates',
                        text: 'services.workOrdersTemplate',
                    });
                    break;
                case ':woTemplateId':
                    pushBreadcrumb({
                        name: 'siteEditTemplate',
                        text: `${woTemplate.value.title}`,
                    });
                    break;
                case 'edit-template':
                    pushBreadcrumb({
                        name: 'siteEditTemplate',
                        text: t('servicesPage.editTemplate'),
                    });
                    break;
                case 'create-template':
                    pushBreadcrumb({
                        name: 'create-template',
                        text: t('servicesPage.createTemplate'),
                    });
                    break;
                case 'billing':
                    pushBreadcrumb({
                        name: 'administration-management-billingRegister',
                        text: 'administration.billing',
                    });
                    break;
                case 'services':
                    pushBreadcrumb({
                        name: 'services',
                        text: 'servicesPage.services',
                    });
                    break;
                case 'services-billing-field-services':
                    pushBreadcrumb({
                        name: 'field-services',
                        text: 'servicesPage.fieldServicebilling',
                    });
                    break;
                case 'laboratory':
                    if (currentPath[0] === 'administration') {
                        return pushBreadcrumb({
                            name: 'services-billing-laboratory',
                            text: 'services.billingLaboratory',
                        });
                    }
                    pushBreadcrumb({
                        name: 'laboratory',
                        text: `pageToDisplay.${routeSegment}`,
                    });
                    break;
                // Common case
                case 'work-orders':
                    if (currentPath.includes('laboratory')) {
                        pushBreadcrumb({
                            name: 'work-orders',
                            text: 'laboratory.labWorkOrders',
                        });
                    }
                    if (currentPath.includes('services')) {
                        pushBreadcrumb({
                            name: 'work-orders',
                            text: 'services.workOrders',
                        });
                    }
                    if (currentPath.includes('sites')) {
                        pushBreadcrumb({
                            name: 'siteWorkOrders',
                            text: 'sitePage.workOrders',
                        });
                    }
                    break;
                // Case laboratory main
                case 'lab-analysis-requests':
                    pushBreadcrumb({
                        name: 'analysis-requests',
                        text: 'laboratory.labAnalysisRequests',
                    });
                    break;
                case 'lab-sample-register':
                    pushBreadcrumb({
                        name: 'samples',
                        text: 'laboratory.samplesRegister',
                    });
                    break;
                case 'lab-reports':
                    pushBreadcrumb({
                        name: 'reports',
                        text: 'laboratory.reports',
                    });
                    break;
                case 'lab-data-transfer':
                    pushBreadcrumb({
                        name: 'data-transfer',
                        text: 'laboratory.dataTransfer',
                    });
                    break;
                case 'lab-xml-reports':
                    pushBreadcrumb({
                        name: 'xml-report',
                        text: 'laboratory.xmlReport',
                    });
                    break;
                // Case administration main
                case 'configuration':
                case 'advancedConfiguration':
                case 'aquassistBilling':
                case 'trends':
                case 'documentLibrary':
                case 'networkTypes':
                case 'componentTypes':
                case 'analysisTypes':
                    pushBreadcrumb({
                        text: `administration.${routeSegment}`,
                    });
                    break;
                case 'analyses':
                    pushBreadcrumb({
                        name: 'administration-configuration-analyses',
                        text: 'administration.analyses',
                    });
                    break;
                case 'categories':
                    pushBreadcrumb({
                        name: 'administration-configuration-analyses-categories',
                        text: 'administration.analysesCategories',
                    });
                    break;
                case 'types':
                    pushBreadcrumb({
                        name: 'administration-configuration-analyses-types',
                        text: 'administration.analysesTypes',
                    });
                    break;
                case 'management':
                    pushBreadcrumb({
                        name: 'administration-management',
                        text: 'administration.management',
                    });
                    break;
                case 'users':
                    pushBreadcrumb({
                        name: 'administration-management-users',
                        text: 'administration.users',
                    });
                    break;
                case ':id':
                    break;
                case 'access-groups':
                    pushBreadcrumb({
                        name: 'administration-management-accessGroups',
                        text: 'administration.accessGroups',
                    });
                    break;
                case 'dynamic-comments':
                    pushBreadcrumb({
                        name: 'administration-management-automaticSentences',
                        text: 'administration.automaticSentences',
                    });
                    break;
                case 'default-cards':
                    pushBreadcrumb({
                        name: 'administration-management-defaultAnalyses',
                        text: 'administration.defaultAnalyses',
                    });
                    break;
                case 'standard-&-limits':
                    pushBreadcrumb({
                        name: 'administration-management-standardFamilies',
                        text: 'administration.standardFamilies',
                    });
                    break;
                case 'contracts':
                    pushBreadcrumb({
                        name: 'administration-management-contracts',
                        text: 'administration.contracts',
                    });
                    break;
                case 'add-ons':
                    pushBreadcrumb({
                        name: 'administration-management-features',
                        text: 'administration.addOns',
                    });
                    break;
                case 'features':
                    pushBreadcrumb({
                        name: 'administration-advance-configuration-features',
                        text: 'administration.features',
                    });
                    break;
                case 'billing-register':
                    pushBreadcrumb({
                        name: 'administration-configuration-billingRegister',
                        text: 'administration.billingRegister',
                    });
                    break;
                case 'info':
                    pushBreadcrumb({
                        text: currentPath.includes('clients')
                            ? `clientPage.${routeSegment}`
                            : `administration.${routeSegment}`,
                    });
                    break;
                case 'service-provider':
                    pushBreadcrumb({
                        name: 'administration-configuration-serviceProvider',
                        text: 'administration.companyInformation',
                    });
                    break;
                case 'service-providers':
                    pushBreadcrumb({
                        name: 'administration-advance-configuration-service-providers',
                        text: 'administration.serviceProviders',
                    });
                    break;
                case 'nodes':
                    pushBreadcrumb({
                        name: 'administration-advance-configuration-nodes',
                        text: 'administration.nodes',
                    });
                    break;
                case 'video-categories':
                    pushBreadcrumb({
                        name: 'administration-advance-configuration-video-categories',
                        text: 'administration.videoCategories',
                    });
                    break;
                case 'device-types':
                    pushBreadcrumb({
                        name: 'deviceTypes',
                        text: 'deviceAnalysisKeys.deviceTypes',
                    });
                    break;
                case 'device-analysis-keys':
                    pushBreadcrumb({
                        name: 'administration-advance-configuration-deviceAnalysisKeys',
                        text: 'administration.deviceAnalysisKeys',
                    });
                    break;
                //case Aquassist Billing
                case 'pricing-management':
                    pushBreadcrumb({
                        name: 'administration-configuration-pricingManagement',
                        text: 'administration.pricingManagement',
                    });
                    break;
                // case Clients
                case ':clientId':
                    pushBreadcrumb({
                        name: 'clientInfo',
                        text: `${client.value.name} [${client.value.accountNumber}]`,
                    });
                    break;
                case ':siteId':
                    pushBreadcrumb({
                        name: 'site-tree',
                        text: `${site.value.name} [${site.value.deliveryAccountNumber}]`,
                    });
                    break;
                case 'manageClientTeam':
                    pushBreadcrumb({
                        name: 'clientManageClientTeam',
                        text: 'clientPage.manageClientTeam',
                    });
                    break;
                case 'devices':
                    pushBreadcrumb({
                        name: 'siteDevices',
                        text: 'sitePage.devices',
                    });
                    break;
                case 'analysis-request':
                    pushBreadcrumb({
                        name: 'siteAnalysisRequest',
                        text: 'sitePage.analysisRequest',
                    });
                    break;
                case 'graphics':
                    pushBreadcrumb({
                        name: 'siteGraphics',
                        text: 'sitePage.graphics',
                    });
                    break;
                case 'cart':
                    pushBreadcrumb({
                        name: 'siteCart',
                        text: 'sitePage.cart',
                    });
                    break;
                case 'document':
                    pushBreadcrumb({
                        name: 'siteDocument',
                        text: 'sites.document.pageName',
                    });
                    break;
                case 'alerts':
                    pushBreadcrumb({
                        name: 'siteAlerts',
                        text: 'sitePage.alerts',
                    });
                    break;
                case 'mass-balance':
                    pushBreadcrumb({
                        name: 'siteMassBalance',
                        text: 'sitePage.massBalance',
                    });
                    break;
                case 'logs':
                    pushBreadcrumb({
                        name: 'siteLogs',
                        text: 'sitePage.logs',
                    });
                    break;
                // case notifications
                case 'notifications':
                    pushBreadcrumb({
                        name: 'notifications',
                        text: 'alertsNotifications.pageName',
                    });
                    break;
                // case teams
                case 'teams':
                    pushBreadcrumb({
                        name: 'teams',
                        text: 'teams.selectTeam',
                    });
                    break;
                // case 'products':
                case 'products':
                    pushBreadcrumb({
                        name: 'siteProducts',
                        text: 'sitePage.products',
                    });
                    break;
                case 'procedures':
                    if (currentPath.includes('administration')) {
                        pushBreadcrumb({
                            name: 'administration-configuration-procedures',
                            text: 'administration.procedures',
                        });
                    } else {
                        pushBreadcrumb({
                            name: 'siteProcedures',
                            text: 'sitePage.procedures',
                        });
                    }
                    break;
                case 'sites':
                    pushBreadcrumb({
                        name: 'clientSites',
                        text: 'pageToDisplay.sites',
                    });
                    break;
                // case Work Orders
                case ':workOrderId':
                    pushBreadcrumb({
                        name: 'workOrders',
                        text: `${workOrder.value.contractNumber}`,
                    });
                    break;
                default:
                    pushBreadcrumb({
                        name: routeSegment,
                        text: `pageToDisplay.${routeSegment}`,
                    });
                    break;
            }
        });
    };

    watch(route, () => {
        buildFromRoute();
    });

    onMounted(() => {
        buildFromRoute();
    });

    const renderItemText = (item: unknown): string => {
        const breadcrumbItem = item as InternalBreadcrumbItem & BreadcrumbItem;
        return breadcrumbItem.noTranslate ? breadcrumbItem.text : t(breadcrumbItem.text);
    };

    const handleItemClick = (item: unknown) => {
        const breadcrumbItem = item as InternalBreadcrumbItem & BreadcrumbItem;
        if (!breadcrumbItem.name && breadcrumbItem.action) {
            emit('send-action', breadcrumbItem.action);
        }
    };
</script>
